import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UserService } from 'app/core/user/user.service';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { SecurityServiceProxy } from 'app/shared/services/security-service.proxy';
import {
    AuthenticationInput,
    ForgetPasswordInput,
    IAuthResponse,
    PasswordRequirementsResponse,
    IVerifySessionResponse,
    RegisterUserInput,
    ResetPasswordInput,
    PasswordComplexityRule,
    VerifyGuestAccessResponse,
    AuthenticateGuestInput,
    GuestUserAuthResponse,
} from 'app/shared/model/security.model';
import { RxdbControlFunctionsService } from 'app/shared/services/rxdb-providers/rxdb-control-functions.service';
import { Router } from '@angular/router';
import { AuthUtils } from './auth.utils';
import { LocalStorageService } from 'app/shared/services/local-storage.service';

type SsoState = { path: string, time: Date };

const minutesHavePassed = (time: Date, minutes: number): boolean => {
    return (Date.now() - time.getTime()) > (minutes * 60 * 1_000);
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private readonly _httpClient = inject(HttpClient);
    private readonly _userService = inject(UserService);
    private readonly _securityService = inject(SecurityServiceProxy);
    private readonly _rxdbControlFunctionsService = inject(RxdbControlFunctionsService);
    private readonly _router = inject(Router);
    private localStorageService = inject(LocalStorageService);

    private SSO_STATE_KEY = 'requestedSsoState';

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param username
     */
    forgotPassword(username: string): Observable<any> {
        const url = document.location.hostname;
        const forgetpasswordInput = new ForgetPasswordInput({ username, url });
        return this._securityService.forgetPassword(forgetpasswordInput);
    }

    /**
     * validate passwordChangeToken
     *
     * @param username
     */
    validatePasswordChangeToken(token: string, username: string): Observable<any> {
        return this._securityService.validatePasswordChangeToken(token, username);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(resetpassword: { token: string; password: string, username: string }): Observable<any> {
        // return this._httpClient.post('api/auth/reset-password', password);
        const resetPasswordInput = new ResetPasswordInput(resetpassword);
        return this._securityService.resetPassword(resetPasswordInput);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { username: string; password: string, keepMeSignedIn: boolean }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        const input = new AuthenticationInput(credentials);
        return this._securityService.authenticate(input).pipe(
            switchMap(async (response: any) => {
                await this._rxdbControlFunctionsService.truncateAllDbData();
                return response;
            }),
            switchMap((response: any) => {
                return this._signInSuccessCallback(response);
            })
        );
    }

    /**
     * Sign in with 2FA
     */
    verifyTwoFactorCode(code: string): Observable<any> {
        return this._securityService.verifyTwoFactorCode(code).pipe(
            switchMap((response: any) => {
                return this._signInSuccessCallback(response);
            })
        );
    }

    /**
     * enable 2fa
     */
    enableTwoFactorAuthCode(code: string): Observable<any> {
        return this._securityService.enableTwoFactor(code).pipe(
            switchMap((response: any) => {
                return this._signInSuccessCallback(response);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    verifySessionToken(path?: string): Observable<any> {
        const requestedSsoState = this.getSsoState();

        return this._securityService.verifySession().pipe(
            catchError(() =>
                // Return false
                of(false)
            ),
            switchMap((response: any) => {
                if (this._checkTwoFactorAuthInProgress(response)) {
                    this.signOut();
                    return of(false);
                }

                if (response.success && !response.user) {
                    if (path && AuthUtils.isSignOutUrl(path)) {
                        this._authenticated = false;
                        this._userService.user = undefined;
                        this._userService.guestUser = undefined;
                        return of(false);
                    }

                    if (response.redirect) {
                        try {
                            // remember where the user was before redirectUrl...
                            this.setSsoState(path);
                            // Basic validation that the URL is of a valid format.
                            const redirectUrl: URL = new URL(response.redirect);
                            window.location.href = redirectUrl.href;
                        } catch (ex) {
                            // this.signOut();
                        }

                        return of(false);
                    }
                }

                if (!response?.user) {
                    // Set the authenticated flag to false
                    this._authenticated = false;
                    this._userService.user = undefined;
                    this._userService.guestUser = response?.guestUser;
                    return of(false);
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // guest user should be removed if it is authenticated user.
                this._userService.guestUser = undefined;

                // if we're coming back from an external Sso request,
                // redirect to the page the user was originally on if within 15 minutes.
                if (requestedSsoState && !minutesHavePassed(requestedSsoState.time, 15)) {
                    this._router.navigate([requestedSsoState.path]);
                }

                // Return true
                return of(true);
            }),
        );
    }

    private getSsoState() {
        let state: SsoState | undefined = undefined;
        try {
            const data: string | null = this.localStorageService.getLocalStorageData(this.SSO_STATE_KEY);
            state = data ? JSON.parse(data) : undefined;
            if (state && typeof state.time == 'string') {
                state.time = new Date(state.time);
            }
        } catch (err) {
            // ignored... leave it undefiend if the data wasn't valid.
        }

        // make sure we clear any already set value as these are single use - if it fails it's over anyway.
        this.localStorageService.removeLocalStorageData(this.SSO_STATE_KEY);

        return state;
    }

    private setSsoState(path: string | undefined) {
        if (path) {
            const state: SsoState = { path: this.safeReturnPath(path), time: new Date() };
            this.localStorageService.setLocalStorageData(this.SSO_STATE_KEY, state);
        }
    }

    private safeReturnPath(path: string): string {
        const authPaths = [
            'confirmation-required',
            'forgot-password',
            'expired-password',
            'reset-password',
            'sign-in',
            'sign-in-error',
            'sign-up'
        ];

        return authPaths.includes(path)
            ? '/dashboard'
            : path;
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._securityService.logout().subscribe({
            next: async (response) => {
                if (response.success) {
                    // Set the authenticated flag to false
                    this._authenticated = false;
                    this._userService.user = null;
                    this._userService.guestUser = null;
                    await this._rxdbControlFunctionsService.truncateAllDbData();
                }
            }
        });
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(input: { email: string, password: string }): Observable<any> {
        const userJson = JSON.stringify({ "EmailAddress": input.email });
        const newUser = new RegisterUserInput({
            userJson,
            password: input.password
        });
        return this._securityService.registerUser(newUser);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(path?: string): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        return this.verifySessionToken(path);
    }

    /**
     * Get Password requirements
     */
    getPasswordRequirements(): Observable<PasswordComplexityRule[]> {
        return this._securityService.getPasswordRequirements().pipe(
            switchMap((response: PasswordRequirementsResponse) => {
                if (response.success) {
                    return of(response.passwordComplexityRules);
                }
            }),
        );
    }

    /**
     * Verify the guest access guid
     */
    verifyGuestAccessGuid(accessGuid: string): Observable<VerifyGuestAccessResponse>
    {
        return this._securityService.verifyGuestAccessGuid(accessGuid).pipe(
            switchMap((response: VerifyGuestAccessResponse) => {
                return of(response);
            }),
        );
    }

    /**
     * Authenticate the guest email
     */
    authenticateGuest(input: AuthenticateGuestInput): Observable<GuestUserAuthResponse>
    {
        return this._securityService.authenticateGuest(input).pipe(
            switchMap((response: GuestUserAuthResponse) => {
                return of(response);
            }),
        );
    }

    /**
     * Verify the guest passcode
     */
    verifyGuestPasscode(passcode: string): Observable<GuestUserAuthResponse>
    {
        return this._securityService.verifyGuestPasscode(passcode).pipe(
            switchMap((response: GuestUserAuthResponse) => {
                return this._guestSignInSuccessCallback(response);
            }),
        );
    }

    private _signInSuccessCallback(response: any): Observable<any> {
        if (response.success && !this._checkTwoFactorAuthRequired(response)) {
            // Set the authenticated flag to true
            this._authenticated = true;
            // Store the user on the user service
            this._userService.user = response.user;
        }
        // Return a new observable with the response
        return of(response);
    }

    private _guestSignInSuccessCallback(response: GuestUserAuthResponse): Observable<GuestUserAuthResponse> {
        if (response.success && !!response.user) {
            // Store the guest user email on the user service
            this._userService.guestUser = response.user;
        }
        // Return a new observable with the response
        return of(response);
    }

    private _checkTwoFactorAuthRequired(response: IAuthResponse) {
        return response.twoFactorActivationRequired || response.twoFactorAuthenticationRequired
    }

    private _checkTwoFactorAuthInProgress(response: IVerifySessionResponse | undefined) {
        if (!response) {
            return false;
        }
        return response.twoFactorActivationRequired || response.twoFactorInProgress
    }
}
